import { motion, AnimatePresence } from 'framer-motion';
import Icon from 'components/AppIcon';
import ThreeDotLoading from 'components/ui/ThreeDotLoading';
import { useGetEmployeeRuleByIdQuery } from 'redux/features/employeeRulesApi';
import CommonDocumentPreview from './CommonDocumentPreview';

function PolicyModal({
  open,
  policy,
  onMarkAsRead,
  isLoading = false,
  isLast = false,
  pendingCount = 0,
}) {
  const isFileType = policy?.type?.toUpperCase() === 'FILE';

  const { data: ruleDetail, isLoading: isFetchingDetail } = useGetEmployeeRuleByIdQuery(
    policy?.id,
    { skip: !open || !isFileType || !!policy?.document },
  );

  const document = policy?.document || ruleDetail?.document;

  if (!policy) return null;

  const handleMarkAsRead = () => {
    if (onMarkAsRead) {
      onMarkAsRead(policy);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="relative w-full max-w-7xl max-h-[80vh] flex flex-col rounded-2xl bg-white shadow-2xl"
            initial={{ y: 40, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-6 py-4">
              <div>
                <h2 className="text-base capitalize font-semibold text-slate-900">
                  {policy.title}
                </h2>
                <p className="mt-0.5 text-xs text-slate-500">
                  Policy details{' '}
                  {pendingCount > 1
                    ? `(${pendingCount} pending documents)`
                    : pendingCount === 1
                      ? '(Last document)'
                      : ''}
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6 custom-scrollbar">
              {policy.type?.toUpperCase() === 'TEXT' ? (
                policy.rules && policy.rules.length > 0 ? (
                  <div className="space-y-2">
                    {policy.rules.map((rule, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2 rounded-xl border border-slate-50 bg-white/50 p-1.5"
                      >
                        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <Icon name="Check" size={10} />
                        </div>
                        <p className="no-copy text-sm leading-relaxed text-slate-600">{rule}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No rules defined for this policy.</p>
                )
              ) : (
                <div className="space-y-3 text-sm text-slate-700">
                  {isFetchingDetail ? (
                    <ThreeDotLoading />
                  ) : document ? (
                    <CommonDocumentPreview document={document} />
                  ) : (
                    <p className="text-sm text-slate-500">No file attached.</p>
                  )}
                </div>
              )}

              <p className="mt-2 rounded-lg border border-amber-200 bg-amber-100 px-3 py-2 text-xs text-amber-800">
                Please read this policy carefully. Click &quot;Mark as read&quot; to confirm that
                you have read and understood the contents.
              </p>
            </div>

            {/* Footer */}
            <div className="flex shrink-0 items-center justify-end border-t border-slate-100 px-6 py-4">
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={handleMarkAsRead}
                  disabled={isLoading}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <>
                    <Icon
                      name={isLoading ? 'Loader' : 'CheckCircle'}
                      className={isLoading ? 'animate-spin' : ''}
                      size={18}
                    />

                    <span>I Agree {isLast && '& Finish'}</span>
                  </>
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default PolicyModal;
